// Initial Load Optimizer
// Aggressively optimizes the first page load experience

class InitialLoadOptimizer {
  constructor() {
    this.isInitialLoad = true;
    this.preloadedImages = new Set();
    this.criticalImages = [];
    this.loadingPromises = new Map();
  }

  // Preload critical images with high priority
  async preloadCriticalImages(images, options = {}) {
    if (!Array.isArray(images) || images.length === 0) return;

    const {
      maxConcurrent = 6,
      timeout = 8000,
      quality = 60, // Lower quality for faster initial load
      width = 600,
      height = 400
    } = options;

    console.log(`🚀 Preloading ${images.length} critical images for initial load...`);

    // Filter out already preloaded images
    const newImages = images.filter(img => !this.preloadedImages.has(img));
    
    if (newImages.length === 0) {
      console.log('✅ All critical images already preloaded');
      return;
    }

    // Process images in chunks for better performance
    const chunks = [];
    for (let i = 0; i < newImages.length; i += maxConcurrent) {
      chunks.push(newImages.slice(i, i + maxConcurrent));
    }

    const startTime = performance.now();

    try {
      // Process first chunk with highest priority
      const firstChunk = chunks[0] || [];
      await this.preloadImageChunk(firstChunk, { quality, width, height, timeout });
      
      // Process remaining chunks with lower priority
      for (let i = 1; i < chunks.length; i++) {
        // Use requestIdleCallback for non-critical chunks
        if (window.requestIdleCallback) {
          await new Promise(resolve => {
            window.requestIdleCallback(() => {
              this.preloadImageChunk(chunks[i], { quality, width, height, timeout })
                .then(resolve)
                .catch(() => resolve()); // Continue even if some fail
            });
          });
        } else {
          await this.preloadImageChunk(chunks[i], { quality, width, height, timeout });
        }
      }

      const loadTime = performance.now() - startTime;
      console.log(`✅ Critical images preloaded in ${Math.round(loadTime)}ms`);
      
    } catch (error) {
      console.warn('⚠️ Some critical images failed to preload:', error);
    }
  }

  // Preload a chunk of images
  async preloadImageChunk(images, options = {}) {
    const { quality, width, height, timeout } = options;
    
    const promises = images.map(async (imageUrl) => {
      if (this.preloadedImages.has(imageUrl)) return imageUrl;
      
      try {
        const optimizedUrl = this.getOptimizedUrl(imageUrl, { quality, width, height });
        await this.preloadSingleImage(optimizedUrl, timeout);
        this.preloadedImages.add(imageUrl);
        return imageUrl;
      } catch (error) {
        console.warn(`Failed to preload image: ${imageUrl}`, error);
        return null;
      }
    });

    const results = await Promise.allSettled(promises);
    return results.filter(result => result.status === 'fulfilled').map(result => result.value);
  }

  // Preload a single image with timeout
  preloadSingleImage(url, timeout = 8000) {
    return new Promise((resolve, reject) => {
      // Check if already loading
      if (this.loadingPromises.has(url)) {
        return this.loadingPromises.get(url);
      }

      const img = new Image();
      const timeoutId = setTimeout(() => {
        reject(new Error(`Image preload timeout: ${url}`));
      }, timeout);

      img.onload = () => {
        clearTimeout(timeoutId);
        this.loadingPromises.delete(url);
        resolve(url);
      };

      img.onerror = () => {
        clearTimeout(timeoutId);
        this.loadingPromises.delete(url);
        reject(new Error(`Image preload failed: ${url}`));
      };

      // Optimize for speed
      img.crossOrigin = 'anonymous';
      img.loading = 'eager'; // High priority loading
      img.src = url;

      const promise = new Promise((resolve, reject) => {
        img.onload = () => {
          clearTimeout(timeoutId);
          this.loadingPromises.delete(url);
          resolve(url);
        };
        img.onerror = () => {
          clearTimeout(timeoutId);
          this.loadingPromises.delete(url);
          reject(new Error(`Image preload failed: ${url}`));
        };
      });

      this.loadingPromises.set(url, promise);
    });
  }

  // Get optimized URL for faster loading
  getOptimizedUrl(originalUrl, options = {}) {
    if (!originalUrl) return originalUrl;
    
    if (originalUrl.startsWith('data:') || originalUrl.startsWith('/')) {
      return originalUrl;
    }

    const { quality = 60, width = 600, height = 400, format = 'webp' } = options;
    
    // For Pinata URLs, add aggressive optimization
    if (originalUrl.includes('pinata.cloud')) {
      const url = new URL(originalUrl);
      url.searchParams.set('w', width);
      url.searchParams.set('h', height);
      url.searchParams.set('q', quality);
      url.searchParams.set('f', format);
      url.searchParams.set('optimize', 'true');
      return url.toString();
    }

    return originalUrl;
  }

  // Preload above-the-fold images immediately
  async preloadAboveTheFold(properties) {
    if (!Array.isArray(properties) || properties.length === 0) return;

    // Get first 6 properties (likely to be above the fold)
    const aboveTheFoldImages = properties
      .slice(0, 6)
      .map(property => property?.metaData?.images?.[0])
      .filter(Boolean);

    if (aboveTheFoldImages.length === 0) return;

    console.log(`🎯 Preloading ${aboveTheFoldImages.length} above-the-fold images...`);
    
    // Use highest priority for above-the-fold images
    await this.preloadCriticalImages(aboveTheFoldImages, {
      maxConcurrent: 6,
      quality: 70, // Slightly higher quality for visible images
      width: 800,
      height: 600,
      timeout: 10000
    });
  }

  // Preload remaining images in background
  async preloadBackgroundImages(properties) {
    if (!Array.isArray(properties) || properties.length === 0) return;

    // Get remaining images (below the fold)
    const backgroundImages = properties
      .slice(6, 20) // Next 14 properties
      .map(property => property?.metaData?.images?.[0])
      .filter(Boolean);

    if (backgroundImages.length === 0) return;

    console.log(`🔄 Preloading ${backgroundImages.length} background images...`);
    
    // Use lower priority for background images
    await this.preloadCriticalImages(backgroundImages, {
      maxConcurrent: 3,
      quality: 50, // Lower quality for background images
      width: 400,
      height: 300,
      timeout: 15000
    });
  }

  // Smart preloading based on user behavior
  async smartPreload(properties, userBehavior = {}) {
    const { scrollPosition = 0, viewportHeight = 800 } = userBehavior;
    
    // Calculate which images are likely to be seen soon
    const visibleRange = Math.ceil((scrollPosition + viewportHeight * 2) / 400); // Approximate property height
    const upcomingImages = properties
      .slice(0, visibleRange + 3) // 3 extra for buffer
      .map(property => property?.metaData?.images?.[0])
      .filter(Boolean);

    await this.preloadCriticalImages(upcomingImages, {
      maxConcurrent: 4,
      quality: 60,
      width: 600,
      height: 400
    });
  }

  // Reset for new page load
  reset() {
    this.isInitialLoad = true;
    this.preloadedImages.clear();
    this.criticalImages = [];
    this.loadingPromises.clear();
  }

  // Get preload statistics
  getStats() {
    return {
      preloadedCount: this.preloadedImages.size,
      loadingCount: this.loadingPromises.size,
      isInitialLoad: this.isInitialLoad
    };
  }
}

// Create singleton instance
const initialLoadOptimizer = new InitialLoadOptimizer();

export default initialLoadOptimizer;
